import type {RevaValuationPurpose} from "@reva/shared/valuation-purpose";

import type {AvmDocumentedEndpoint} from "../api/api-constants-avm";
import {
    AvmDocumentationListApiInterface,
    AvmGeneralDocumentationApiInterface,
    AvmParametersApiInterface,
    AvmReleaseNotesApiInterface,
    AvmValidationReportApiInterface,
    IndicationDocumentationApiInterface,
    MicroMacroDocumentationApiInterface,
    RevaManualApiInterface,
    TimelinesDocumentationApiInterface,
} from "../api/interface/avm-documentation";
import {LocationPdfApiInterface, SnapshotPdfApiInterface, ValuationPdfApiInterface} from "../api/interface/reva-pdf";
import type {DownloadedFile} from "../api/response";
import {openBlobForDownload} from "../util/browserUtil";
import {selectCredentials} from "./authSlice";
import {reloadDocumentsIfMissing} from "./documentsThunk";
import type {LocationPdfType, PdfOrientation, ValuationPdfType} from "./pdfConstants";
import {showPdfPreview} from "./pdfPreviewSlice";
import {selectRevaId} from "./persistenceSlice";
import type {RevaThunkAction} from "./revaHooks";

/** Downloads the timelines documentation PDF from the AVM API. */
export const fetchTimelinesDocumentation =
    (): RevaThunkAction<Promise<DownloadedFile>> =>
    async (_, getState, {serviceApi}) =>
        new TimelinesDocumentationApiInterface().execute(serviceApi, selectCredentials(getState())!);

/** Downloads the general AVM documentation PDF from the AVM API (#19432). */
export const fetchAvmDocumentation =
    (): RevaThunkAction<Promise<DownloadedFile>> =>
    async (_, getState, {serviceApi}) =>
        new AvmGeneralDocumentationApiInterface().execute(serviceApi, selectCredentials(getState())!);

/** Fetches the list of AVM documentations with their upload date from the AVM API (#19302). */
export const fetchAvmDocumentationList =
    (): RevaThunkAction<Promise<unknown>> =>
    async (_, getState, {serviceApi}) =>
        new AvmDocumentationListApiInterface().execute(serviceApi, selectCredentials(getState())!);

/** Downloads the AVM release notes PDF from the AVM API (#19432). */
export const fetchAvmReleaseNotes =
    (): RevaThunkAction<Promise<DownloadedFile>> =>
    async (_, getState, {serviceApi}) =>
        new AvmReleaseNotesApiInterface().execute(serviceApi, selectCredentials(getState())!);

/**
 * Downloads the AVM parameter overview PDF from the AVM API (#19302).
 * @param language language of the overview
 */
export const fetchAvmParameters =
    (language: "de" | "en"): RevaThunkAction<Promise<DownloadedFile>> =>
    async (_, getState, {serviceApi}) =>
        new AvmParametersApiInterface(language).execute(serviceApi, selectCredentials(getState())!);

/** Downloads the AVM validation report PDF from the AVM API (#19302). */
export const fetchAvmValidationReport =
    (): RevaThunkAction<Promise<DownloadedFile>> =>
    async (_, getState, {serviceApi}) =>
        new AvmValidationReportApiInterface().execute(serviceApi, selectCredentials(getState())!);

/**
 * Downloads the method documentation PDF of the residential locations (micro/macro location) from
 * the AVM API (#19302).
 */
export const fetchMicroMacroDocumentation =
    (): RevaThunkAction<Promise<DownloadedFile>> =>
    async (_, getState, {serviceApi}) =>
        new MicroMacroDocumentationApiInterface().execute(serviceApi, selectCredentials(getState())!);

/**
 * Downloads the REVA user manual PDF for one purpose from the AVM API (#19302).
 * @param purpose purpose the manual is written for
 * @param language "de" or "en"
 */
export const fetchRevaManual =
    (purpose: RevaValuationPurpose, language: string): RevaThunkAction<Promise<DownloadedFile>> =>
    async (_, getState, {serviceApi}) =>
        new RevaManualApiInterface(purpose, language).execute(serviceApi, selectCredentials(getState())!);

/**
 * Downloads the method documentation PDF of one indication endpoint (#19432).
 * @param endpoint indication endpoint whose documentation is wanted
 */
export const fetchIndicationDocumentation =
    (endpoint: AvmDocumentedEndpoint): RevaThunkAction<Promise<DownloadedFile>> =>
    async (_, getState, {serviceApi}) =>
        new IndicationDocumentationApiInterface(endpoint).execute(serviceApi, selectCredentials(getState())!);

/**
 * Downloads one of the three valuation reports of the loaded valuation.
 * @param type report to render
 */
export const fetchValuationPdf =
    (type: ValuationPdfType): RevaThunkAction<Promise<DownloadedFile>> =>
    async (_, getState, {serviceApi}) => {
        const state = getState();
        return new ValuationPdfApiInterface(requireRevaId(selectRevaId(state)), type).execute(
            serviceApi,
            selectCredentials(state)!,
        );
    };

/**
 * Downloads one of the three valuation reports rendered from a stored lifecycle snapshot.
 * @param snapshotNumber number of the snapshot to render
 * @param type report to render
 */
export const fetchSnapshotPdf =
    (snapshotNumber: number, type: ValuationPdfType): RevaThunkAction<Promise<DownloadedFile>> =>
    async (dispatch, getState, {serviceApi}) => {
        const state = getState();
        const downloadedFile = await new SnapshotPdfApiInterface(
            requireRevaId(selectRevaId(state)),
            snapshotNumber,
            type,
        ).execute(serviceApi, selectCredentials(state)!);
        // The backend creates a missing snapshot PDF and archives it in the order on the way. A filename the
        // document list does not know means it was created just now, so reload it in the background; a
        // re-delivered document changes nothing.
        void dispatch(reloadDocumentsIfMissing(downloadedFile?.filename));
        return downloadedFile;
    };

/**
 * Downloads one of the map/location PDFs of the loaded valuation.
 * @param type map to render
 * @param orientation orientation, for the types that have one
 */
export const fetchPdfForType =
    (type: LocationPdfType, orientation?: PdfOrientation): RevaThunkAction<Promise<DownloadedFile>> =>
    async (_, getState, {serviceApi}) => {
        const state = getState();
        return new LocationPdfApiInterface(requireRevaId(selectRevaId(state)), type, orientation).execute(
            serviceApi,
            selectCredentials(state)!,
        );
    };

/**
 * Löst denselben Ablauf aus wie der "PDF drucken"-Button: blendet die
 * Vorschau-Box in den "atmenden" Generierungs-Zustand, erzeugt das Karten-PDF
 * für type/orientation und startet den Download. Wirft weiter, damit der
 * Aufrufer (Button-Toast bzw. Box) den Fehler behandeln kann; der
 * "generating"-Zustand wird in jedem Fall wieder zurückgesetzt.
 *
 * Wird sowohl vom {@code DownloadMapPdfButton} als auch von der klickbaren
 * Fläche in der PDF-Druckbereich-Box (OpenLayersMap) genutzt.
 * @param type map to render
 * @param orientation orientation, for the types that have one
 */
export const downloadPdfForType =
    (type: LocationPdfType, orientation?: PdfOrientation): RevaThunkAction<Promise<void>> =>
    async (dispatch) => {
        // Box (erneut) einblenden und in den "atmenden" Generierungs-Zustand versetzen.
        dispatch(showPdfPreview({type, orientation, generating: true}));
        try {
            const {blob, filename} = await dispatch(fetchPdfForType(type, orientation));
            const fallbackFilename = `${type + (orientation ? `_${orientation}` : "")}.pdf`;
            openBlobForDownload(blob, filename ?? fallbackFilename);
        } finally {
            dispatch(showPdfPreview({type, orientation, generating: false}));
        }
    };

/**
 * Die PDF-Endpunkte hängen an einer geladenen Bewertung. Der Aufrufer fängt den Fehler ohnehin ab,
 * um die Meldungen des PDF-Dienstes anzuzeigen - deshalb hier werfen statt still nichts zu tun.
 */
function requireRevaId(revaId: string | null): string {
    if (!revaId) {
        throw new Error("Cannot download a PDF without a loaded valuation");
    }
    return revaId;
}
